// src/components/PresentationMode.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import '../styles/PresentationMode.css';

export const PresentationMode = () => {
  const { collection_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [artworks, setArtworks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collectionInfo, setCollectionInfo] = useState(null);
  const [error, setError] = useState(null);

  // Vérifier accès et charger données
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Destinataire d'un lien de partage (sans compte) : accès borné par le jeton, via une
        // fonction dédiée (jeton valide, non révoqué, non expiré ; champs limités, sans nom).
        const sharedToken = sessionStorage.getItem('presentation_session_token');
        if (sharedToken) {
          const { data: shared } = await supabase.rpc('get_presentation_by_token', {
            p_session_token: sharedToken,
          });
          if (shared && shared.collection_id === collection_id) {
            const list = shared.artworks || [];
            if (list.length === 0) {
              setError("Cette collection ne contient aucune œuvre");
              setLoading(false);
              return;
            }
            setCollectionInfo({ id: shared.collection_id, title: shared.title, collectorName: 'Collection privée' });
            setArtworks(list);
            setLoading(false);
            return;
          }
        }

        if (!user) {
          setError("Vous devez être connecté");
          setLoading(false);
          return;
        }

        console.log('[PresentationMode] Chargement collection:', collection_id);

        // 1. Récupérer la collection
        const { data: collectionData, error: collectionError } = await supabase
          .from('collections')
          .select('id, title, client_id')
          .eq('id', collection_id)
          .single();

        if (collectionError) {
          console.error('[PresentationMode] Erreur collection:', collectionError);
          throw collectionError;
        }

        console.log('[PresentationMode] Collection trouvée:', collectionData);

        // 2. Vérifier l'accès
        const userRole = user.user_metadata?.role || user.app_metadata?.role || null;
        
        const hasAccess = 
          collectionData.client_id === user.id || // Propriétaire
          userRole === 'expert' ||                 // Expert (Art Advisor)
          userRole === 'family_office' ||
          userRole === 'team' ||
          userRole === 'admin';

        if (!hasAccess) {
          setError("Vous n'avez pas accès à cette collection");
          setLoading(false);
          return;
        }

        // 3. Récupérer les artworks
        const { data: artworksData, error: artworksError } = await supabase
          .from('artworks')
          .select('id, title, artist, year, image_url, medium, dimensions')
          .eq('collection_id', collection_id)
          .order('created_at', { ascending: true });

        if (artworksError) {
          console.error('[PresentationMode] Erreur artworks:', artworksError);
          throw artworksError;
        }

        console.log('[PresentationMode] Artworks trouvées:', artworksData?.length);

        if (!artworksData || artworksData.length === 0) {
          setError("Cette collection ne contient aucune œuvre");
          setLoading(false);
          return;
        }

        // 4. Nom du collectionneur (seulement si propriétaire)
        let collectorName = 'Collection privée';
        
        if (collectionData.client_id === user.id) {
          // Seulement si c'est le propriétaire, on essaie de récupérer son nom
          try {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', collectionData.client_id)
              .limit(1);

            if (profileData && profileData.length > 0) {
              const p = profileData[0];
              collectorName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Collection privée';
            }
          } catch (err) {
            console.warn('[PresentationMode] Impossible de récupérer le profil:', err);
          }
        }

        setCollectionInfo({
          ...collectionData,
          collectorName: collectorName
        });

        setArtworks(artworksData);
        setLoading(false);

      } catch (err) {
        console.error('[PresentationMode] Erreur fatale:', err);
        setError(err.message || 'Erreur de chargement');
        setLoading(false);
      }
    };

    fetchData();
  }, [collection_id, user]);

  // Navigation clavier
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => 
      prev < artworks.length - 1 ? prev + 1 : prev
    );
  }, [artworks.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'Escape') navigate(-1);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [goToNext, goToPrevious, navigate]);

  // Support swipe mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) goToNext();
    if (isRightSwipe) goToPrevious();
  };

  // États de chargement
  if (loading) {
    return (
      <div className="presentation-loading">
        <div className="spinner" />
        <p>Chargement de la collection...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="presentation-error">
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="btn-back">
          Retour
        </button>
      </div>
    );
  }

  if (!artworks || artworks.length === 0) {
    return (
      <div className="presentation-error">
        <p>Aucune œuvre dans cette collection</p>
        <button onClick={() => navigate(-1)} className="btn-back">
          Retour
        </button>
      </div>
    );
  }

  const currentArtwork = artworks[currentIndex];

  return (
    <div 
      className="presentation-container"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Image principale */}
      <div className="presentation-image-wrapper">
        <img 
          src={currentArtwork.image_url || '/images/artworks/Vignette_oeuvre_Image_Not_Available.png'}
          alt={currentArtwork.title || 'Œuvre'}
          className="presentation-image"
          onError={(e) => {
            e.currentTarget.src = '/images/artworks/Vignette_oeuvre_Image_Not_Available.png';
          }}
        />
      </div>

      {/* Informations artwork */}
      <div className="presentation-info">
        <h2 className="presentation-artist">
          {currentArtwork.artist || 'Artiste inconnu'}
          {currentArtwork.year && `, ${currentArtwork.year}`}
        </h2>
        <h3 className="presentation-title">
          {currentArtwork.title || 'Sans titre'}
        </h3>
        {currentArtwork.medium && (
          <p className="presentation-medium">{currentArtwork.medium}</p>
        )}
        {currentArtwork.dimensions && (
          <p className="presentation-dimensions">{currentArtwork.dimensions}</p>
        )}
      </div>

      {/* Navigation */}
      <div className="presentation-controls">
        <button 
          onClick={goToPrevious}
          disabled={currentIndex === 0}
          className="presentation-nav-btn"
          aria-label="Œuvre précédente"
        >
          ←
        </button>

        <span className="presentation-counter">
          {currentIndex + 1} / {artworks.length}
        </span>

        <button 
          onClick={goToNext}
          disabled={currentIndex === artworks.length - 1}
          className="presentation-nav-btn"
          aria-label="Œuvre suivante"
        >
          →
        </button>

        <button 
          onClick={() => navigate(-1)}
          className="presentation-close-btn"
        >
          ⊗ Quitter
        </button>
      </div>

      {/* Zones cliquables gauche/droite (desktop) */}
      <div 
        className="presentation-click-zone left"
        onClick={goToPrevious}
        aria-hidden="true"
      />
      <div 
        className="presentation-click-zone right"
        onClick={goToNext}
        aria-hidden="true"
      />

      {/* Info collection (coin supérieur) */}
      <div className="presentation-header">
        <span className="presentation-collection-name">
          {collectionInfo?.title || 'Collection'}
        </span>
        {collectionInfo?.collectorName && (
          <span className="presentation-collector-name">
            {collectionInfo.collectorName}
          </span>
        )}
      </div>
    </div>
  );
};

export default PresentationMode;